<script>
import { mapGetters } from "vuex";
import CRMActivitiesAPI from "dashboard/api/crm/activities";

export default {
  name: "DealCard",
  props: {
    deal: { type: Object, required: true },
    mobile: { type: Boolean, default: false },
  },
  emits: [
    "edit-deal",
    "create-activity",
    "complete-activity",
    "delete-activity",
    "open-activity-detail",
  ],
  computed: {
    ...mapGetters("labels", ["getLabels"]),
    // FORK: deal.labels llega como array de títulos; resolver color desde el store.
    dealLabels() {
      const titles = this.deal.labels || [];
      return titles.map((title) => {
        const match = this.getLabels.find((l) => l.title === title);
        return { title, color: match?.color || "#94a3b8" };
      });
    },
    // Actividades a usar (cargadas o las del deal)
    currentActivities() {
      return this.allActivities.length > 0
        ? this.allActivities
        : this.deal.recent_activities || [];
    },

    // Actividades visibles en la página actual
    visibleActivities() {
      const startIndex = this.currentActivityPage * this.activitiesPerPage;
      const endIndex = startIndex + this.activitiesPerPage;
      const visible = this.currentActivities.slice(startIndex, endIndex);

      // Solo mostrar placeholders si no hemos cargado todas las actividades aún
      if (
        visible.length < this.activitiesPerPage &&
        this.hasMoreActivities &&
        startIndex < this.deal.activities_total_count
      ) {
        const placeholderCount = Math.min(
          this.activitiesPerPage - visible.length,
          this.deal.activities_total_count - (startIndex + visible.length),
        );
        for (let i = 0; i < placeholderCount; i++) {
          visible.push({
            id: `placeholder-${startIndex + visible.length + i}`,
            isPlaceholder: true,
            subject: "Cargando...",
            activity_type: "task",
          });
        }
      }

      return visible;
    },

    // Total de páginas de actividades (basado en el total real de actividades)
    totalActivityPages() {
      // Usar SIEMPRE activities_total_count como referencia
      const totalCount = this.deal.activities_total_count || 0;

      if (totalCount === 0) {
        return 0;
      }
      return Math.ceil(totalCount / this.activitiesPerPage);
    },

    // Si hay navegación disponible (hay más actividades de las que se pueden mostrar)
    hasActivityNavigation() {
      // Usar SIEMPRE activities_total_count como referencia, no las actividades cargadas
      const totalCount = this.deal.activities_total_count || 0;
      return totalCount > this.activitiesPerPage;
    },

    // Si se puede ir a la página anterior
    canGoPrevious() {
      return this.currentActivityPage > 0;
    },

    // Si se puede ir a la página siguiente
    canGoNext() {
      const totalPages = this.totalActivityPages;
      return this.currentActivityPage < totalPages - 1;
    },

    // Si hay más actividades disponibles para cargar
    hasMoreActivities() {
      return (
        this.deal.activities_total_count &&
        this.deal.activities_total_count > this.currentActivities.length
      );
    },
  },
  data() {
    return {
      showActivityMenu: false,
      currentActivityPage: 0, // Página actual de actividades (índice basado en 0)
      activitiesPerPage: 2, // Número de actividades por página
      allActivities: [], // Todas las actividades cargadas
      isLoadingActivities: false, // Estado de carga de actividades
    };
  },

  watch: {
    // Reset pagination y actividades cuando cambie el deal
    "deal.id": {
      handler() {
        this.resetActivityPagination();
        this.allActivities = []; // Limpiar actividades cargadas
      },
    },
    "deal.recent_activities": {
      handler() {
        this.resetActivityPagination();
        this.allActivities = []; // Limpiar actividades cargadas
      },
      deep: true,
    },
  },

  mounted() {
    document.addEventListener("click", this.handleClickOutside);

    // Reset pagination cuando el deal cambia
    this.resetActivityPagination();
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    handleClick() {
      this.$emit("edit-deal", this.deal);
    },

    toggleActivityMenu(event) {
      event.stopPropagation();
      this.showActivityMenu = !this.showActivityMenu;
    },

    createNewActivity(event) {
      event.stopPropagation();
      this.$emit("create-activity", { deal: this.deal, activityType: "task" });
      this.showActivityMenu = false;
    },

    // Cerrar menú al hacer click fuera
    handleClickOutside(event) {
      if (!this.$refs.activityMenu?.contains(event.target)) {
        this.showActivityMenu = false;
      }
    },

    openActivityDetail(activity, event) {
      event.stopPropagation(); // Evitar que se dispare el click del deal
      this.$emit("open-activity-detail", { activity, deal: this.deal });
    },

    getActivityTypeInSpanish(type) {
      const types = {
        task: "Tarea",
        call: "Llamada",
        meeting: "Reunión",
        email: "Email",
        note: "Nota",
      };
      return types[type] || type;
    },

    getActivityIcon(type) {
      const icons = {
        task: "📋",
        call: "📞",
        meeting: "🤝",
        email: "📧",
        note: "📝",
      };
      return icons[type] || "📋";
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("es-ES", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    // Métodos para navegación de actividades
    async nextActivityPage(event) {
      event.stopPropagation();

      const totalPages = this.totalActivityPages;
      if (this.currentActivityPage < totalPages - 1) {
        this.currentActivityPage++;

        // Si necesitamos cargar más actividades y no las tenemos aún
        const nextPageStart = this.currentActivityPage * this.activitiesPerPage;
        if (
          this.hasMoreActivities &&
          nextPageStart >= this.currentActivities.length
        ) {
          await this.loadAllActivities();
        }
      }
    },

    async prevActivityPage(event) {
      event.stopPropagation();
      if (this.currentActivityPage > 0) {
        this.currentActivityPage--;
      }
    },

    resetActivityPagination() {
      this.currentActivityPage = 0;
    },

    // Método para cargar todas las actividades del deal
    async loadAllActivities(event) {
      if (event) event.stopPropagation();
      if (
        this.isLoadingActivities ||
        !this.deal.id ||
        this.allActivities.length >= this.deal.activities_total_count
      )
        return;

      try {
        this.isLoadingActivities = true;

        const response = await CRMActivitiesAPI.get({ deal_id: this.deal.id });

        // La respuesta puede ser un array directamente o estar en response.data
        const activities = Array.isArray(response)
          ? response
          : response.data || [];
        this.allActivities = activities;
      } catch (error) {
        // Fallback a las actividades que ya tenemos
        this.allActivities = this.deal.recent_activities || [];
      } finally {
        this.isLoadingActivities = false;
      }
    },

    // Método para obtener el emoji del método de pago
    getPaymentMethodEmoji(paymentMethod) {
      const emojis = {
        bank_transfer: "🏦",
        cash: "💵",
        debit_card: "💳",
        credit_card: "💰",
        qr_code: "📱",
        electronic_check: "📄",
      };
      return emojis[paymentMethod] || "";
    },
  },
};
</script>

<template>
  <div>
    <div
      class="p-3 bg-white dark:bg-slate-700 rounded shadow text-sm cursor-pointer hover:shadow-md transition-shadow relative"
      :data-id="deal.id"
      @click="handleClick"
    >
      <!-- Header con título y menú de actividades -->
      <div class="flex justify-between items-start">
        <h4 class="font-medium truncate flex-1 mr-2" :title="deal.title">
          {{ deal.title }}
        </h4>

        <!-- Menú de Actividades (ícono pequeño) -->
        <div ref="activityMenu" class="relative">
          <button
            class="flex items-center justify-center w-7 h-7 text-gray-700 hover:text-gray-900 dark:text-slate-200 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-600 rounded transition-colors bg-gray-50 dark:bg-slate-700 border border-gray-300 dark:border-slate-500"
            title="Actividades"
            @click="toggleActivityMenu"
          >
            <!-- Ícono simple de más (+) -->
            <span class="text-lg font-bold leading-none">+</span>
            <!-- Contador de actividades pendientes -->
            <span
              v-if="deal.activities_pending_count > 0"
              class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1 min-w-[16px] h-4 flex items-center justify-center text-[10px]"
            >
              {{ deal.activities_pending_count }}
            </span>
          </button>

          <!-- Dropdown Menu Simple con estilo del stage -->
          <div
            v-if="showActivityMenu"
            class="absolute right-0 top-8 bg-white dark:bg-n-solid-3 rounded-lg shadow-lg border border-n-weak z-50 min-w-[160px]"
          >
            <button
              class="w-full text-left px-4 py-2 text-sm text-n-slate-12 hover:bg-n-slate-9/5 transition-all cursor-pointer border-0 outline-none rounded-lg flex items-center"
              @click="createNewActivity"
            >
              <svg
                class="w-4 h-4 inline mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Nueva Actividad
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="!mobile"
        class="mt-1 text-xs text-gray-600 dark:text-slate-200"
      >
        <span v-if="deal.primary_contact">{{ deal.primary_contact.name }}</span>
        <span v-else class="text-red-500">No contact</span>
      </div>

      <!-- Mobile: puntos de color por etiqueta (señal visual sin texto) -->
      <div
        v-if="mobile && dealLabels.length > 0"
        class="mt-1 flex gap-1"
      >
        <span
          v-for="label in dealLabels"
          :key="label.title"
          class="w-1.5 h-1.5 rounded-full flex-shrink-0"
          :style="{ backgroundColor: label.color }"
          :title="label.title"
        />
      </div>

      <!-- FORK: etiquetas del deal -->
      <div v-if="!mobile && dealLabels.length > 0" class="mt-1 flex flex-wrap gap-1">
        <span
          v-for="label in dealLabels"
          :key="label.title"
          class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium text-white truncate max-w-[120px]"
          :style="{ backgroundColor: label.color }"
          :title="label.title"
        >
          {{ label.title }}
        </span>
      </div>

      <!-- Mostrar productos (múltiples o singular) -->
      <div
        v-if="!mobile && deal.products && deal.products.length > 0"
        class="mt-1 text-xs"
      >
        <!-- Si hay un solo producto, mostrar inline -->
        <div
          v-if="deal.products.length === 1"
          class="text-blue-600 dark:text-blue-400 truncate"
          :title="deal.products[0].name"
        >
          📦 {{ deal.products[0].name }}
        </div>
        <!-- Si hay múltiples productos, mostrar badges -->
        <div v-else class="flex flex-wrap gap-1">
          <span
            v-for="product in deal.products"
            :key="product.id"
            :title="product.name + ' - ' + product.formatted_price"
            class="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 truncate max-w-[120px]"
          >
            📦 {{ product.name }}
          </span>
        </div>
      </div>
      <!-- Fallback: mostrar producto singular (deprecated, para compatibilidad) -->
      <div
        v-else-if="!mobile && deal.product"
        class="mt-1 text-xs text-blue-600 dark:text-blue-400 truncate"
        :title="deal.product.name"
      >
        📦 {{ deal.product.name }}
      </div>

      <div
        v-if="!mobile || deal.amount_cents"
        class="mt-1 flex justify-between text-xs"
      >
        <span v-if="!mobile">{{ deal.owner?.name || "Sin asignar" }}</span>
        <span v-if="deal.amount_cents" :class="mobile ? 'font-medium' : ''">
          <span v-if="deal.payment_method" class="mr-1">{{
            getPaymentMethodEmoji(deal.payment_method)
          }}</span>{{ deal.currency }} {{ (deal.amount_cents / 100).toFixed(2) }}
        </span>
      </div>

      <!-- Actividades básicas - al final como subtarjeta -->
      <div
        v-if="!mobile && deal.recent_activities && deal.recent_activities.length > 0"
        class="mt-2 pt-2"
        style="border-top: 1px solid #e5e7eb"
      >
        <!-- Header de actividades con navegación -->
        <div
          v-if="hasActivityNavigation"
          class="flex justify-between items-center mb-2"
        >
          <div class="flex items-center">
            <span
              class="text-xs text-slate-600 dark:text-slate-400 font-medium"
            >
              <span v-if="isLoadingActivities"
class="mr-1 text-blue-500"
                >⏳</span>
              {{ currentActivityPage + 1 }}/{{ totalActivityPages }}
            </span>
          </div>
          <div class="flex items-center space-x-2">
            <!-- Flecha IZQUIERDA elegante -->
            <button
              :disabled="!canGoPrevious || isLoadingActivities"
              class="w-7 h-7 flex items-center justify-center rounded-full shadow-sm transition-all duration-200"
              :class="{
                'bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 border border-slate-300 hover:border-slate-400 cursor-pointer hover:shadow-md':
                  canGoPrevious && !isLoadingActivities,
                'bg-slate-50 text-slate-300 border border-slate-200 cursor-not-allowed':
                  !canGoPrevious || isLoadingActivities,
              }"
              title="Página anterior"
              @click="prevActivityPage"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <!-- Flecha DERECHA elegante -->
            <button
              :disabled="!canGoNext || isLoadingActivities"
              class="w-7 h-7 flex items-center justify-center rounded-full shadow-sm transition-all duration-200"
              :class="{
                'bg-blue-100 hover:bg-blue-200 text-blue-700 hover:text-blue-900 border border-blue-300 hover:border-blue-400 cursor-pointer hover:shadow-md':
                  canGoNext && !isLoadingActivities,
                'bg-slate-50 text-slate-300 border border-slate-200 cursor-not-allowed':
                  !canGoNext || isLoadingActivities,
              }"
              title="Página siguiente"
              @click="nextActivityPage"
            >
              <svg
                v-if="!isLoadingActivities"
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <div
                v-else
                class="w-3 h-3 border border-slate-400 border-t-transparent rounded-full animate-spin"
              />
            </button>
          </div>
        </div>

        <!-- Lista de actividades visibles -->
        <div
          v-for="activity in visibleActivities"
          :key="activity.id"
          class="text-xs mb-0.5 px-2 py-1 rounded transition-all duration-150 flex justify-between items-center"
          :class="{
            'text-gray-600 dark:text-slate-400 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-700 dark:hover:text-blue-300 border border-transparent hover:border-blue-200 dark:hover:border-blue-700':
              !activity.isPlaceholder,
            'text-gray-400 dark:text-slate-500 opacity-50 animate-pulse':
              activity.isPlaceholder,
          }"
          @click="
            activity.isPlaceholder ? null : openActivityDetail(activity, $event)
          "
        >
          <span class="truncate flex-1">
            {{ getActivityIcon(activity.activity_type) }} {{ activity.subject }}
          </span>
          <span v-if="!activity.isPlaceholder" class="flex-shrink-0 ml-2">
            <span
              v-if="activity.completed_at"
              class="text-green-600 dark:text-green-400"
            >
              ✅
            </span>
            <span v-else class="text-orange-600 dark:text-orange-400">
              ⏳
            </span>
          </span>
        </div>

        <!-- Indicador de total de actividades si no hay navegación -->
        <!-- Indicador removido para limpiar la vista -->
      </div>
    </div>
  </div>
</template>
